import { supabase } from '../config/supabase';
import { progressStageService, ProgressStage } from './progressStageService';

export interface ProgressStageSetting {
  id: string;
  org_id: number;
  value: number;
  label: string;
  description?: string | null;
  color?: string | null;
  order_index: number;
  created_at: string;
  updated_at: string;
  created_by?: string | null;
}

export const progressStageSettingsService = {
  /**
   * Get settings for an org. Returns DB settings if present, otherwise defaults.
   */
  async getStages(orgId: number): Promise<ProgressStage[]> {
    try {
      const { data, error } = await supabase
        .from('progress_stage_settings')
        .select('*')
        .eq('org_id', orgId)
        .order('order_index', { ascending: true });

      if (error && error.code !== 'PGRST116') {
        console.warn('Error fetching progress stage settings; using defaults:', error);
        return progressStageService.getProgressStages(orgId);
      }

      if (data && data.length > 0) {
        return data.map((row: ProgressStageSetting) => ({
          value: Number(row.value),
          label: row.label,
          description: row.description || '',
          color: row.color || undefined,
        }));
      }

      return progressStageService.getProgressStages(orgId);
    } catch (e) {
      console.warn('Error in getStages; using defaults:', e);
      return progressStageService.getProgressStages(orgId);
    }
  },

  /**
   * Get current user's org stages
   */
  async getCurrentUserStages(): Promise<ProgressStage[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return progressStageService.getProgressStages(0);
      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('id', user.id)
        .maybeSingle();
      if (!profile?.org_id) return progressStageService.getProgressStages(0);
      return this.getStages(Number(profile.org_id));
    } catch {
      return progressStageService.getProgressStages(0);
    }
  },

  /**
   * Save or update a stage row
   */
  async upsertStage(row: {
    orgId: number;
    value: number;
    label: string;
    description?: string | null;
    color?: string | null;
    orderIndex: number;
    userId?: string;
  }): Promise<void> {
    const { orgId, value, label, description, color, orderIndex, userId } = row;
    const { error } = await supabase
      .from('progress_stage_settings')
      .upsert({
        org_id: orgId,
        value,
        label,
        description: description ?? null,
        color: color ?? null,
        order_index: orderIndex,
        updated_at: new Date().toISOString(),
        created_by: userId || null,
      }, {
        onConflict: 'org_id,value'
      });
    if (error) throw error;
  },

  /**
   * Delete a stage row by value
   */
  async deleteStage(orgId: number, value: number): Promise<void> {
    const { error } = await supabase
      .from('progress_stage_settings')
      .delete()
      .eq('org_id', orgId)
      .eq('value', value);
    if (error) throw error;
  },

  /**
   * Replace all stages for an org in one call (convenience).
   */
  async replaceAllStages(orgId: number, stages: Array<Omit<ProgressStageSetting, 'id' | 'created_at' | 'updated_at' | 'created_by'>>): Promise<void> {
    // naive replace: delete all then insert all
    const { error: delErr } = await supabase
      .from('progress_stage_settings')
      .delete()
      .eq('org_id', orgId);
    if (delErr) throw delErr;
    if (stages.length === 0) return;
    const payload = stages.map(s => ({
      ...s,
      org_id: orgId,
      updated_at: new Date().toISOString(),
    }));
    const { error: insErr } = await supabase
      .from('progress_stage_settings')
      .insert(payload);
    if (insErr) throw insErr;
  }
};


